import { STORAGE_KEYS } from './constants';

/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} - True if token is expired
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= exp;
  } catch (error) {
    return true;
  }
};

/**
 * Validate if a token exists and is not expired
 * @param {string} token - JWT token
 * @returns {boolean} - True if token is valid
 */
export const isTokenValid = (token) => {
  return token && !isTokenExpired(token);
};

/**
 * Format date to a readable string
 * @param {string|Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';

  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  try {
    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(
      new Date(date)
    );
  } catch (error) {
    return date.toString();
  }
};

/**
 * Format date to a short date string (without time)
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatShortDate = (date) => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Truncate text to a specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} - Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format error message from API response
 * @param {object} error - Error object
 * @returns {string} - Formatted error message
 */
export const formatErrorMessage = (error) => {
  if (typeof error === 'string') return error;

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.message) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
};

/**
 * Get initials from a name
 * @param {string} name - Full name
 * @returns {string} - Initials
 */
export const getInitials = (name) => {
  if (!name) return '';

  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
};

/**
 * Build query string from parameters object
 * @param {object} params - Parameters object
 * @returns {string} - Query string
 */
export const buildQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';

  const filteredParams = Object.entries(params).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );

  if (filteredParams.length === 0) return '';

  const queryString = new URLSearchParams(
    filteredParams.map(([key, value]) => [key, value.toString()])
  ).toString();

  return `?${queryString}`;
};

/**
 * Debounce function
 * @param {function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {function} - Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Capitalize first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} - Capitalized string
 */
export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Check if user has a specific role
 * @param {object} user - User object
 * @param {string|array} roles - Role(s) to check
 * @returns {boolean} - True if user has role
 */
export const hasRole = (user, roles) => {
  if (!user || !user.role) return false;

  if (Array.isArray(roles)) {
    return roles.includes(user.role);
  }

  return user.role === roles;
};

/**
 * Generate a random color based on a string (for avatars)
 * @param {string} str - String to generate color from
 * @returns {string} - Hex color code
 */
export const stringToColor = (str) => {
  if (!str) return '#6366f1';

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colors = [
    '#6366f1', // indigo
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#f59e0b', // amber
    '#10b981', // emerald
    '#3b82f6', // blue
    '#06b6d4', // cyan
    '#f97316', // orange
  ];

  return colors[Math.abs(hash) % colors.length];
};

/**
 * Format phone number to US format: (XXX) XXX-XXXX
 * @param {string} phoneNumber - Phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Handle different lengths
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // If more than 10 digits, only use first 10
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

/**
 * Strip phone number formatting (remove non-digits)
 * @param {string} phoneNumber - Formatted phone number
 * @returns {string} - Phone number with only digits
 */
export const stripPhoneFormatting = (phoneNumber) => {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Validate US phone number
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} - True if valid US phone number
 */
export const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return true; // Optional field
  const cleaned = stripPhoneFormatting(phoneNumber);
  return cleaned.length === 10 && /^\d{10}$/.test(cleaned);
};

/**
 * Display formatted phone number (for display purposes)
 * @param {string} phoneNumber - Phone number to display
 * @returns {string} - Formatted phone number for display
 */
export const displayPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return 'N/A';
  const cleaned = stripPhoneFormatting(phoneNumber);

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return phoneNumber; // Return as-is if not 10 digits
};
